import React, { useCallback } from 'react';
import { useProducts } from '../../../../hooks/useProducts';
import { UserProductType, UserProduct } from '../../../../types/product.types';
import { ProductPickerItemProps } from './ProductPickerItem';
import { useWeaveThemeContext } from '../../theme/WeaveContext';
import { Svg } from '../../../../components/Svg';
import { useProjects } from '../../../../hooks/useProjects';

import AssetBlack from './svgs/AssetBlack.svg';
import AssetWhite from './svgs/AssetWhite.svg';
import InsightBlack from './svgs/InsightBlack.svg';
import InsightWhite from './svgs/InsightWhite.svg';
import PlantBlack from './svgs/PlantBlack.svg';
import PlantWhite from './svgs/PlantWhite.svg';
import ModelBlack from './svgs/ModelBlack.svg';
import ModelWhite from './svgs/ModelWhite.svg';

import SettingsIcon from '@mui/icons-material/Settings';
import { UserProject } from 'types/project.types';

type ProductListItemHash = {
	[key in UserProductType]: ProductPickerItemProps;
};

export const useAvailableProductListItems = (): ProductPickerItemProps[] => {
	const { isWeaveTheme } = useWeaveThemeContext();
	const { activeProject } = useProjects();
	const { availableProducts } = useProducts();

	const switchAction = useCallback(
		(product: UserProduct, activeProject: UserProject | undefined) => {
			if (!activeProject) return;
			window.location.href = product.url;
		},
		[activeProject, availableProducts],
	);
	//@ts-ignore
	const productListItemHash: (product: UserProduct) => ProductListItemHash =
		//@ts-ignore
		useCallback<ProductListItemHash>(
			(product: UserProduct) => ({
				[UserProductType.ASSET]: {
					type: UserProductType.ASSET,
					text: 'Info360 Asset',
					icon: <Svg svg={isWeaveTheme ? AssetBlack : AssetWhite} />,
					switchAction: () => {
						switchAction(product, activeProject);
					},
				},
				[UserProductType.INSIGHT]: {
					type: UserProductType.INSIGHT,
					text: 'Info360 Insight',
					icon: (
						<Svg svg={isWeaveTheme ? InsightBlack : InsightWhite} />
					),
					switchAction: () => {
						switchAction(product, activeProject);
					},
				},
				[UserProductType.PLANT]: {
					type: UserProductType.PLANT,
					text: 'Info360 Plant',
					icon: <Svg svg={isWeaveTheme ? PlantBlack : PlantWhite} />,
					switchAction: () => {
						switchAction(product, activeProject);
					},
				},
				[UserProductType.MODEL]: {
					type: UserProductType.MODEL,
					text: 'Info360 Model',
					icon: <Svg svg={isWeaveTheme ? ModelBlack : ModelWhite} />,
					switchAction: () => {
						switchAction(product, activeProject);
					},
				},
			}),
			[isWeaveTheme, activeProject],
		);

	const reduceAvailableProductItems = (
		acc: ProductPickerItemProps[],
		product: UserProduct,
	) =>
		productListItemHash(product)[product.name]
			? [...acc, productListItemHash(product)[product.name]]
			: acc;

	return availableProducts.reduce(reduceAvailableProductItems, []);
};

export const useAvailableAdminListItems = (): ProductPickerItemProps[] => {
	const {
		isWeaveTheme,
		weaveThemeConsumer: { resolvedRoles },
	} = useWeaveThemeContext();

	const color = React.useMemo(
		() =>
			isWeaveTheme ? resolvedRoles['colorScheme.text.default'] : 'white',
		[isWeaveTheme],
	);

	const { availableAdminSections } = useProducts();
	if (availableAdminSections.length === 0) return [];
	else {
		return [
			{
				//@ts-ignore
				type: 'projectAdministration',
				text: 'Project Admin',
				icon: <SettingsIcon style={{ color }} />,
				switchAction: () => {
					window.location.href = availableAdminSections[0].url;
				},
			},
		];
	}
};
